import { clone, concat, map, orderBy } from 'es-toolkit/compat';
import { useState } from 'react';

import { AddButton } from 'components/profile/AddButton';
import { DEFAULT_CITY_URL } from 'components/profile/DestinationCard';
import { MultiChipSelect } from 'ui';
import { StoreType } from '@tock/shared/utils/connect';
import { Typography } from 'omakase';
import { allMetroAreasSelector } from 'selectors/appSelector';
import { core } from '@tock/coretypes';
import { cropImageUrl } from '@tock/images';
import { defaultMemoize } from 'reselect';
import { getCountryFromMetro } from 'utils/profileUtils';
import { useSelector } from 'react-redux';
import CircleImage from 'components/profile/CircleImage';
import RemoveListOptions, { Item } from 'components/profile/RemoveListOptions';

function mapStateToProps(state: StoreType) {
  return {
    allCities: allMetroAreasSelector(state),
  };
}

interface Props {
  cityValues: core.IMetroArea[];
  onChange(days: core.IMetroArea[]): void;
}

interface ConnectProps {
  allCities?: core.IMetroArea[];
}

const getAvailableOptions = (allCities: core.IMetroArea[]): SelectOption[] => {
  const cityOptions = map(allCities, (cityOption, index) => {
    return {
      key: cityOption.id || index,
      value: cityOption.id || '',
      label: cityOption.name || '',
    };
  });
  // Sort alphabetically
  return orderBy(cityOptions, (option) => option.label, ['asc']);
};

const memoGetAvailableOptions = defaultMemoize(getAvailableOptions);

const generateCityDisplay = (item: Item<core.IMetroArea>) => {
  const { value } = item;
  const destinationImageUrl =
    (value.image && value.image.length > 0 && value.image[0].imageUrl) || DEFAULT_CITY_URL;
  const country = getCountryFromMetro(value);

  return (
    <div className="EditProfile-displayCity">
      <div className="EditProfile-cityImage-container">
        <CircleImage
          className="EditProfile-cityImage"
          imageUrl={destinationImageUrl && cropImageUrl(destinationImageUrl, 72, 72)}
        />
      </div>
      <div>
        <div>
          <Typography color="inherit" component="span" variant="body02Strong">
            {value.name}
          </Typography>
        </div>
        <div className="EditProfile-country">
          <Typography color="inherit" component="span" variant="body02">
            {country}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const DestinationEditor = ({ cityValues, onChange }: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const { allCities } = useSelector<StoreType, ConnectProps>(mapStateToProps);

  const citiesListed = map(cityValues, (city, index) => {
    return { key: city.id || index, value: city };
  });

  const handleSelectCity = (selectedCityOption: SelectOption | null) => {
    if (selectedCityOption) {
      const clonedValues = clone(cityValues);
      const selectedCityIndex = clonedValues.findIndex(
        (selectedCity) => selectedCity.id === selectedCityOption.key
      );
      if (selectedCityIndex === -1) {
        onChange(
          concat(
            clonedValues,
            allCities?.find((metroArea) => metroArea.id === selectedCityOption.value)
          ).filter((metroArea) => !!metroArea) as core.IMetroArea[]
        );
      } else {
        clonedValues.splice(selectedCityIndex, 1);
        onChange(clonedValues);
      }
    }
  };

  const citiesAvailable: SelectOption[][] = allCities ? [memoGetAvailableOptions(allCities)] : [[]];

  return (
    <>
      <RemoveListOptions<core.IMetroArea>
        onChange={onChange}
        generateInnerComponent={(item) => generateCityDisplay(item)}
        items={citiesListed}
      />
      {showSearch ? (
        <MultiChipSelect
          open={showSearch}
          id="dream-destinations"
          inputLabel="Select your favorite cities"
          itemGroups={citiesAvailable}
          onChange={handleSelectCity}
          selectedItems={citiesListed.map((metroAreaOption) => ({
            key: metroAreaOption.key,
            label: metroAreaOption.value.name || '',
            value: metroAreaOption.value.id || '',
          }))}
          onClose={() => setShowSearch(false)}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
          }}
        />
      ) : (
        <AddButton itemText="cities" onClick={() => setShowSearch(true)} />
      )}
    </>
  );
};

export default DestinationEditor;
